/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Register Router - Routes invitation tokens to the appropriate registration flow
 *
 * Logic:
 * - Admin invitations (organizationId=null): Access token only → /signup?token=X
 * - Family/Org invitations: Directed to specific form based on role
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect, useRef } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Loader2, AlertCircle, Lock, Mail, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import AuthLayout from '../components/auth/AuthLayout'
import MarketingPanel from '../components/auth/MarketingPanel'
import { useAuthStore } from '../stores/authStore'
import { getVisitorId } from '../services/campaignService'
import { createLogger } from '../utils/logger'

const logger = createLogger('RegisterRouter')

const RegisterRouter = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')
  const referralCode = searchParams.get('ref')
  const { user, isAuthenticated, checkAuth } = useAuthStore()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [registrationEnabled, setRegistrationEnabled] = useState(true)
  const [configLoading, setConfigLoading] = useState(true)
  const [referralValid, setReferralValid] = useState(false)
  const [referralLoading, setReferralLoading] = useState(!!referralCode)

  // Ref to prevent double execution in React Strict Mode
  const isAcceptingRef = useRef(false)

  // Check registration config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch('/api/config/public')
        if (response.ok) {
          const config = await response.json()
          setRegistrationEnabled(config.registrationEnabled)
        }
      } catch (err) {
        logger.error('Failed to fetch config:', err)
      } finally {
        setConfigLoading(false)
      }
    }
    fetchConfig()
  }, [])

  // Validate referral or campaign code if present
  useEffect(() => {
    if (!referralCode) {
      setReferralLoading(false)
      return
    }

    const validateCode = async () => {
      try {
        // Use unified resolver that checks both referral and campaign codes
        const response = await fetch(`/api/campaigns/resolve/${encodeURIComponent(referralCode)}`)
        if (response.ok) {
          const data = await response.json()
          // Valid if it's a referral or active campaign
          const isValid = data.success && data.data?.valid === true
          setReferralValid(isValid)

          // If it's a campaign, record a hit (fire-and-forget)
          if (isValid && data.data?.type === 'campaign') {
            const visitorId = getVisitorId()
            fetch('/api/campaigns/hit', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                campaignCode: referralCode,
                visitorId,
                landingPage: window.location.pathname,
                queryParams: Object.fromEntries(new URLSearchParams(window.location.search))
              })
            }).catch(() => {}) // fire-and-forget
          }
        } else {
          setReferralValid(false)
        }
      } catch (err) {
        logger.error('Failed to validate tracking code:', err)
        setReferralValid(false)
      } finally {
        setReferralLoading(false)
      }
    }
    validateCode()
  }, [referralCode])

  // Route based on invitation type
  useEffect(() => {
    logger.debug('[RegisterRouter] useEffect triggered', {
      token,
      isAuthenticated,
      user: user?.email,
      isAccepting: isAcceptingRef.current
    })

    // Guard against double execution - must be checked SYNCHRONOUSLY before any async work
    if (isAcceptingRef.current) {
      logger.debug('[RegisterRouter] Already processing invitation, skipping')
      return
    }

    if (!token) {
      logger.debug('[RegisterRouter] No token, setting loading false')
      setLoading(false)
      return
    }

    // Set flag IMMEDIATELY before any async operations to prevent race conditions
    isAcceptingRef.current = true

    const loadInvitationAndRoute = async () => {
      try {
        logger.debug('[RegisterRouter] Fetching invitation...')
        const response = await fetch(`/api/invitations/${token}`)

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.message || 'Invalid or expired invitation')
        }

        const data = await response.json()

        if (data.success && data.data) {
          const invitation = data.data

          // If user is already logged in, check if they can accept this invitation directly
          logger.debug('[RegisterRouter] Checking auth state', {
            isAuthenticated,
            userEmail: user?.email,
            invitationEmail: invitation.email
          })
          if (user) {
            const userEmail = user.email?.toLowerCase()
            const invitationEmail = invitation.email?.toLowerCase()

            logger.debug('[RegisterRouter] User is authenticated, comparing emails', {
              userEmail,
              invitationEmail
            })
            if (userEmail === invitationEmail) {
              // Email matches! Accept invitation directly
              logger.debug('[RegisterRouter] Emails match! Accepting invitation...')
              try {
                const acceptResponse = await fetch(`/api/invitations/${token}/accept`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({})
                })

                if (acceptResponse.ok) {
                  await checkAuth() // Refresh user data
                  toast.success(
                    `You've joined ${invitation.organization?.name || 'the organization'}!`
                  )
                  navigate('/dashboard', { replace: true })
                  return
                } else {
                  const errorData = await acceptResponse.json()
                  toast.error(errorData.message || 'Failed to accept invitation')
                  navigate('/dashboard', { replace: true })
                  return
                }
              } catch (acceptError) {
                logger.error('Error accepting invitation:', acceptError)
                toast.error('Failed to accept invitation')
                navigate('/dashboard', { replace: true })
                return
              }
            } else {
              // Email mismatch - show error and redirect to dashboard
              toast.error(
                `This invitation is for ${invitation.email}. You're logged in as ${user.email}.`
              )
              navigate('/dashboard', { replace: true })
              return
            }
          }

          // Not authenticated - continue with normal routing

          // System-wide admin invitation (no organizationId) = access token only
          // Let user choose their account type at /signup
          if (!invitation.organizationId) {
            navigate(`/signup?token=${token}`, { replace: true })
            return
          }

          // Family/Org invitation = directed to specific form based on role
          if (invitation.role === 'guardian') {
            navigate(`/register/guardian?token=${token}`, { replace: true })
          } else {
            navigate(`/register/student?token=${token}`, { replace: true })
          }
        } else {
          throw new Error('Invalid invitation')
        }
      } catch (err) {
        logger.error('Error loading invitation:', err)
        setError(err.message || 'Failed to load invitation')
        setLoading(false)
        // Reset flag on error so user can retry
        isAcceptingRef.current = false
      }
    }

    loadInvitationAndRoute()

    // Cleanup: reset flag if component unmounts or dependencies change
    return () => {
      isAcceptingRef.current = false
    }
  }, [token, navigate, isAuthenticated, user, checkAuth])

  // If no token and user is logged in, redirect to dashboard
  if (!token && user) {
    return <Navigate to="/dashboard" replace />
  }

  // Show error for invalid referral code
  if (referralCode && !referralLoading && !referralValid) {
    return (
      <AuthLayout
        marketingContent={<MarketingPanel />}
        formTitle="Invalid Referral"
        formSubtitle="We couldn't verify your referral link"
      >
        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full mb-4">
              <AlertCircle className="w-8 h-8 text-red-500 dark:text-red-400" />
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              This referral link is invalid or has expired. The person who shared it may have run
              out of invites.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Please contact the person who shared this link to request a new invitation.
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                Already have an account?
              </span>
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
            >
              Sign in to your account
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </AuthLayout>
    )
  }

  // If no token/referral and registration is disabled, show disabled message
  // Valid referral codes bypass the registration disabled check
  if (!token && !configLoading && !registrationEnabled && !referralLoading && !referralValid) {
    return (
      <AuthLayout
        marketingContent={<MarketingPanel />}
        formTitle="Registration Unavailable"
        formSubtitle="Open registration is currently disabled"
      >
        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
              <Lock className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              New account registration requires an invitation. If you have received an invitation
              email, please use the link provided in that email to create your account.
            </p>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800 dark:text-blue-200 text-left">
                  Check your email for an invitation link, or contact your organization
                  administrator to request access.
                </p>
              </div>
            </div>
          </motion.div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                Already have an account?
              </span>
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
            >
              Sign in to your account
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </AuthLayout>
    )
  }

  // If no token and registration is enabled (or has valid referral), redirect to signup landing
  if (!token && !configLoading && !referralLoading) {
    // Pass referral code to signup page if present and valid
    if (referralCode && referralValid) {
      return <Navigate to={`/signup?ref=${referralCode}`} replace />
    }
    return <Navigate to="/signup" replace />
  }

  // Show error state
  if (error) {
    return (
      <AuthLayout
        marketingContent={<MarketingPanel />}
        formTitle="Invalid Invitation"
        formSubtitle="We couldn't verify your invitation"
      >
        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full mb-4">
              <AlertCircle className="w-8 h-8 text-red-500 dark:text-red-400" />
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              The invitation link may have expired or already been used. Please contact the person
              who invited you to request a new invitation.
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                Already have an account?
              </span>
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
            >
              Sign in to your account
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </AuthLayout>
    )
  }

  // Show loading state while fetching invitation
  return (
    <AuthLayout
      marketingContent={<MarketingPanel />}
      formTitle="Loading Invitation"
      formSubtitle="Please wait..."
    >
      <div className="flex flex-col items-center justify-center py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Verifying your invitation...</p>
        </motion.div>
      </div>
    </AuthLayout>
  )
}

export default RegisterRouter
